import React, { useEffect, useState } from 'react';
import { ArrowUp, ArrowDown, Trash2, Plus } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

/* Phase 4A — builder settings for Grouped Attribute Rating and Contact Preference. */

export const MAX_CRITERIA = 10;
const newId = () => `attr_${Math.random().toString(36).slice(2, 10)}`;

const CriterionRow = ({ item, index, count, onLabel, onMove, onRemove }) => {
  const [label, setLabel] = useState(item.label);
  useEffect(() => { setLabel(item.label); }, [item.label]);
  return (
    <div className="flex items-center gap-1.5" data-testid={`criterion-row-${index}`}>
      <span className="text-[10px] font-bold text-[#94A3B8] w-4 text-right">{index + 1}</span>
      <Input value={label} onChange={(e) => setLabel(e.target.value)} onBlur={() => label.trim() && label !== item.label && onLabel(label.trim())}
        className="text-sm h-8" placeholder="Criterion label" data-testid={`criterion-label-${index}`} />
      <Button type="button" variant="ghost" size="icon" className="h-7 w-7" disabled={index === 0} onClick={() => onMove(-1)} data-testid={`criterion-up-${index}`} aria-label="Move up"><ArrowUp className="w-3.5 h-3.5" /></Button>
      <Button type="button" variant="ghost" size="icon" className="h-7 w-7" disabled={index === count - 1} onClick={() => onMove(1)} data-testid={`criterion-down-${index}`} aria-label="Move down"><ArrowDown className="w-3.5 h-3.5" /></Button>
      <Button type="button" variant="ghost" size="icon" className="h-7 w-7" disabled={count <= 1} onClick={onRemove} data-testid={`criterion-remove-${index}`} aria-label="Remove"><Trash2 className="w-3.5 h-3.5 text-red-400" /></Button>
    </div>
  );
};

export function AttributeRatingsSettings({ question, onUpdate }) {
  const items = Array.isArray(question.attributes) ? question.attributes : [];
  const [instruction, setInstruction] = useState(question.attr_instruction || '');
  useEffect(() => { setInstruction(question.attr_instruction || ''); }, [question.attr_instruction]);
  const save = (next) => onUpdate(question.id, { attributes: next });
  const move = (i, d) => {
    const next = [...items];
    [next[i], next[i + d]] = [next[i + d], next[i]];
    save(next);
  };
  return (
    <div className="space-y-3 border-t border-[#E2E8F0] pt-4" data-testid="attribute-ratings-settings">
      <h4 className="text-xs font-bold text-[#64748B] uppercase">Criteria (1–5 stars each)</h4>
      <div>
        <label className="text-xs font-medium text-[#64748B] block mb-1">Instruction (optional)</label>
        <Input value={instruction} onChange={(e) => setInstruction(e.target.value)}
          onBlur={() => instruction !== (question.attr_instruction || '') && onUpdate(question.id, { attr_instruction: instruction })}
          className="text-sm" placeholder="e.g. Tap the stars for each item." data-testid="attr-instruction-input" />
      </div>
      <div className="space-y-1.5">
        {items.map((it, i) => (
          <CriterionRow key={it.id} item={it} index={i} count={items.length}
            onLabel={(label) => save(items.map((x, j) => (j === i ? { ...x, label } : x)))}
            onMove={(d) => move(i, d)}
            onRemove={() => save(items.filter((_, j) => j !== i))} />
        ))}
      </div>
      <Button type="button" variant="outline" size="sm" disabled={items.length >= MAX_CRITERIA}
        onClick={() => save([...items, { id: newId(), label: `Criterion ${items.length + 1}` }])} data-testid="criterion-add-btn">
        <Plus className="w-3.5 h-3.5 mr-1" /> Add criterion
      </Button>
      <p className="text-[10px] text-[#94A3B8]" data-testid="criteria-count">{items.length} of {MAX_CRITERIA} criteria. All render on one screen; Required means every criterion must be rated.</p>
    </div>
  );
}

const LINKABLE = { mobile_or_email: 'Mobile or Email', email: 'Email', contact_info: 'Contact Information' };
const plain = (html) => String(html || '').replace(/<[^>]*>/g, '').slice(0, 50);

export function ContactPreferenceSettings({ question, questions, onUpdate }) {
  const cps = question.contact_preference_settings || {};
  const linkedId = cps.linked_question_id || '';
  const saveCps = (patch) => onUpdate(question.id, { contact_preference_settings: { ...cps, ...patch } });
  const candidates = (questions || []).filter(q => LINKABLE[q.type] && q.id !== question.id);
  const idx = (questions || []).findIndex(q => q.id === question.id);
  const sameScreen = !!linkedId && !!question.inline_with_prev && questions[idx - 1]?.id === linkedId;
  return (
    <div className="space-y-3 border-t border-[#E2E8F0] pt-4" data-testid="contact-preference-settings">
      <h4 className="text-xs font-bold text-[#64748B] uppercase">Contact Preference</h4>
      <p className="text-[10px] text-[#94A3B8]">Respondents choose No or Yes (neither is pre-selected). Use <code>[Brand Name]</code> in the question text to show the client's brand name.</p>
      <div>
        <label className="text-xs font-medium text-[#64748B] block mb-1">Linked contact field</label>
        <Select value={linkedId || 'none'} onValueChange={(v) => saveCps({ linked_question_id: v === 'none' ? null : v })}>
          <SelectTrigger className="text-sm" data-testid="contact-pref-link-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Not linked</SelectItem>
            {candidates.map(q => (
              <SelectItem key={q.id} value={q.id} data-testid={`contact-pref-link-option-${q.id}`}>{LINKABLE[q.type]}: {plain(q.text) || 'Untitled'}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-[10px] text-[#94A3B8] mt-1">"Yes" makes this field required; "No" leaves it optional.{candidates.length === 0 ? ' Add a Mobile or Email question first.' : ''}</p>
      </div>
      <div className="flex items-center justify-between gap-3">
        <label className="text-xs font-medium text-[#64748B]">Ask for Name when Yes</label>
        <Switch checked={!!cps.ask_name_on_yes} onCheckedChange={(v) => saveCps({ ask_name_on_yes: v })} data-testid="contact-pref-ask-name-toggle" />
      </div>
      <div className="flex items-center justify-between gap-3">
        <label className={`text-xs font-medium ${cps.ask_name_on_yes ? 'text-[#64748B]' : 'text-[#CBD5E1]'}`}>Require Name when Yes</label>
        <Switch checked={cps.require_name_on_yes !== false} disabled={!cps.ask_name_on_yes}
          onCheckedChange={(v) => saveCps({ require_name_on_yes: v })} data-testid="contact-pref-require-name-toggle" />
      </div>
      <div className="flex items-center justify-between gap-3">
        <label className="text-xs font-medium text-[#64748B]">Show on the same screen as the previous question</label>
        <Switch checked={!!question.inline_with_prev} onCheckedChange={(v) => onUpdate(question.id, { inline_with_prev: v })} data-testid="contact-pref-inline-toggle" />
      </div>
      {linkedId && !sameScreen && (
        <p className="text-[10px] text-amber-700 bg-amber-50 rounded px-2 py-1.5" data-testid="contact-pref-same-screen-warning">
          Tip: place the linked field directly before this question and turn on "Show on the same screen" so respondents who choose Yes can fill it in right away.
        </p>
      )}
    </div>
  );
}
